import { useEffect, useRef } from 'react';

/**
 * Picks up a new deploy without anybody re-installing the app.
 *
 * The Android shell loads the live site rather than bundling a copy, so a web
 * deploy already reaches the phone — `index.html` is served `no-cache,
 * must-revalidate` and the hashed assets are immutable, which is exactly right.
 * The gap is not caching, it is *reloading*: an ordinary browser tab gets closed
 * and reopened many times a day, but this app runs behind a foreground service
 * that deliberately keeps the process alive, so a technician's WebView can stay
 * open across a whole shift and keep running the build it started with.
 *
 * So the check is tied to coming back to the app, which is the moment a reload
 * costs least. `index.html`'s ETag changes on every deploy — it is the same
 * value the browser already revalidates against — so comparing it needs no build
 * id, no new endpoint and no version file to keep in step.
 */

/**
 * How long the app must have been away before a reload is considered.
 *
 * Glancing at a notification and coming straight back is not a moment to throw
 * away a half-typed message. A minute away means the person genuinely left,
 * which is when losing an unsaved form is unlikely and running a stale build for
 * the rest of a shift is the worse outcome.
 */
const AWAY_LONG_ENOUGH_MS = 60_000;

async function currentBuildTag(): Promise<string | null> {
  try {
    // HEAD, so this costs a header exchange rather than the document. no-store
    // on the request itself, or the check could be answered by the very cache
    // it is meant to see past.
    const response = await fetch('/', { method: 'HEAD', cache: 'no-store' });
    return response.headers.get('etag') ?? response.headers.get('last-modified');
  } catch {
    // Offline, which is the ordinary state of a phone between subscriber sites.
    // Not knowing whether a deploy happened is not a reason to do anything.
    return null;
  }
}

export function useDeployReload(): void {
  const loadedTag = useRef<string | null>(null);
  const hiddenSince = useRef<number | null>(null);

  useEffect(() => {
    void currentBuildTag().then((tag) => {
      loadedTag.current = tag;
    });

    const onVisibility = () => {
      if (document.hidden) {
        hiddenSince.current = Date.now();
        return;
      }

      const away = hiddenSince.current === null ? 0 : Date.now() - hiddenSince.current;
      hiddenSince.current = null;

      if (away < AWAY_LONG_ENOUGH_MS) return;

      void currentBuildTag().then((tag) => {
        // Only when both are known and genuinely differ. A missing header on
        // either side is not evidence of a deploy, and reloading on it would
        // put the app in a loop the moment a proxy stopped sending ETags.
        if (!tag || !loadedTag.current || tag === loadedTag.current) return;

        window.location.reload();
      });
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
}
